import React from 'react';
import { useIntl } from 'react-intl';
import { COVER_EXPORT_PX, type CoverTextBox } from 'fym-shared/src/music24/coverDoc';
import { TypedMemo } from '../../common/types';
import { useCoverEditor } from '../CoverEditorContext';
import { coverEditorMessages } from '../coverEditorMessages';
import { InspectorRow } from './controls/InspectorRow';
import { Slider } from './controls/Slider';
import { Toggle } from './controls/Toggle';
import { useTextPatch } from './useTextPatch';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the cover editor; each part uses its own slice of it */
import styles from '../CoverEditor.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface TextLookFieldsProps {
  box: CoverTextBox;
}

/** The largest blur and line width of the sliders, as shares of the side. */
const MAX_SHADOW = 0.08;
const MAX_OUTLINE = 0.02;
const MAX_GLOW = 0.1;
const DEFAULT_SHADOW = { colour: '#000000', blurShare: 0.02, offsetXShare: 0, offsetYShare: 0.004 };

const percent = (value: number, max: number): number => Math.round((value / max) * 100);

/** The second block of the text inspector (design cover-editor b1): shadow, outline, glow, opacity, rotation, position. */
const TextLookFieldsComponent: React.FC<TextLookFieldsProps> = ({ box }) => {
  const intl = useIntl();
  const { state, readOnly } = useCoverEditor();
  const patch = useTextPatch(box.id);
  const accent = state.doc.palette[state.doc.palette.length - 1] ?? '#ffffff';
  const shadowLabel = intl.formatMessage(coverEditorMessages.textShadow);
  const outlineLabel = intl.formatMessage(coverEditorMessages.textOutline);
  const glowLabel = intl.formatMessage(coverEditorMessages.textGlow);

  return (
    <div className={styles.inspectorGrid}>
      <InspectorRow label={shadowLabel}>
        <span className={styles.toggleRow}>
          <Toggle
            label={shadowLabel}
            isOn={box.shadow !== null}
            disabled={readOnly}
            onChange={(isOn) => patch({ shadow: isOn ? DEFAULT_SHADOW : null })}
          />
          <Slider
            label={shadowLabel}
            value={percent(box.shadow?.blurShare ?? 0, MAX_SHADOW)}
            min={0}
            max={100}
            display={String(percent(box.shadow?.blurShare ?? 0, MAX_SHADOW))}
            disabled={readOnly || box.shadow === null}
            onChange={(value) =>
              patch({ shadow: { ...(box.shadow ?? DEFAULT_SHADOW), blurShare: (value / 100) * MAX_SHADOW } }, 'shadow')
            }
          />
        </span>
      </InspectorRow>
      <InspectorRow label={outlineLabel}>
        <span className={styles.toggleRow}>
          <Toggle
            label={outlineLabel}
            isOn={box.outline !== null}
            disabled={readOnly}
            onChange={(isOn) => patch({ outline: isOn ? { colour: '#000000', widthShare: 0.003 } : null })}
          />
          <Slider
            label={outlineLabel}
            value={percent(box.outline?.widthShare ?? 0, MAX_OUTLINE)}
            min={0}
            max={100}
            display={String(percent(box.outline?.widthShare ?? 0, MAX_OUTLINE))}
            disabled={readOnly || box.outline === null}
            onChange={(value) =>
              patch(
                { outline: { colour: box.outline?.colour ?? '#000000', widthShare: (value / 100) * MAX_OUTLINE } },
                'outline'
              )
            }
          />
        </span>
      </InspectorRow>
      <InspectorRow label={glowLabel}>
        <span className={styles.toggleRow}>
          <Toggle
            label={glowLabel}
            isOn={box.glow !== null}
            disabled={readOnly}
            onChange={(isOn) => patch({ glow: isOn ? { colour: accent, blurShare: 0.03 } : null })}
          />
          <Slider
            label={glowLabel}
            value={percent(box.glow?.blurShare ?? 0, MAX_GLOW)}
            min={0}
            max={100}
            display={String(percent(box.glow?.blurShare ?? 0, MAX_GLOW))}
            disabled={readOnly || box.glow === null}
            onChange={(value) =>
              patch({ glow: { colour: box.glow?.colour ?? accent, blurShare: (value / 100) * MAX_GLOW } }, 'glow')
            }
          />
        </span>
      </InspectorRow>
      <InspectorRow label={intl.formatMessage(coverEditorMessages.textOpacity)}>
        <Slider
          label={intl.formatMessage(coverEditorMessages.textOpacity)}
          value={Math.round(box.opacity * 100)}
          min={5}
          max={100}
          display={String(Math.round(box.opacity * 100))}
          disabled={readOnly}
          onChange={(value) => patch({ opacity: value / 100 }, 'opacity')}
        />
      </InspectorRow>
      <InspectorRow label={intl.formatMessage(coverEditorMessages.textRotate)}>
        <Slider
          label={intl.formatMessage(coverEditorMessages.textRotate)}
          value={Math.round(box.rotationDeg)}
          min={-180}
          max={180}
          display={`${Math.round(box.rotationDeg)}°`}
          disabled={readOnly}
          onChange={(rotationDeg) => patch({ rotationDeg }, 'rotation')}
        />
      </InspectorRow>
      <InspectorRow label={intl.formatMessage(coverEditorMessages.textPosition)}>
        <span className={styles.positionRow}>
          <span className={styles.fieldMono}>X {Math.round(box.x * COVER_EXPORT_PX)}</span>
          <span className={styles.fieldMono}>Y {Math.round(box.y * COVER_EXPORT_PX)}</span>
        </span>
      </InspectorRow>
    </div>
  );
};

export const TextLookFields = TypedMemo(TextLookFieldsComponent);
